import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

const tooltipSx = {
    backgroundColor: theme => theme.palette.background.paper,
    border: theme => `1px solid ${theme.palette.divider}`,
    padding: '5px 8px'
};

const formatCurrency = (value) => (
    new Intl.NumberFormat('fr-BE', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
    }).format(value || 0)
);

const CustomToolTip = ({ active, payload, label, freq }) => {
    if (!active || !payload) {
        return null;
    }

    let title = new Date(label).toLocaleString('default', { day: 'numeric', month: 'numeric', year: 'numeric' });
    if (freq === 'yearly') {
        title = new Date(label).getFullYear();
    }
    if (freq === 'monthly') {
        title = new Date(label).toLocaleDateString('default', { month: 'long', year: 'numeric' });
    }
    if (freq === 'quarterly') {
        const date = new Date(label);
        const quarter = Math.floor(date.getMonth() / 3) + 1;
        title = `Q${quarter} ${date.getFullYear()}`;
    }

    return (
        <Box sx={tooltipSx}>
            <Typography sx={{ fontWeight: 'bold' }}>{title}</Typography>
            {payload.map((item, i) => (
                <Typography key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Box component="span" sx={{ color: item.color }}>
                        {item.name}:
                    </Box>
                    <Box component="span">{formatCurrency(item.value)}</Box>
                </Typography>
            ))}
        </Box>
    );
};

const InvoicingRevenueChart = ({ data, freq = 'monthly', showLegend = true }) => {
    const theme = useTheme();

    const formatDate = (datetime) => {
        const date = new Date(datetime);

        if (freq === 'yearly') {
            return date.getFullYear();
        }
        if (freq === 'monthly') {
            return date.toLocaleString('default', { month: 'short' });
        }
        if (freq === 'quarterly') {
            const quarter = Math.floor(date.getMonth() / 3) + 1;
            return `Q${quarter}`;
        }
        return date.toLocaleString('default', { day: 'numeric', month: 'numeric' });
    };

    if (!data || data.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                <Typography color="text.secondary">Aucune donnée disponible</Typography>
            </Box>
        );
    }

    const cursorStyle = { fill: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : theme.palette.background.default };
    const lineColor = theme.palette.mode === 'dark' ? '#535353' : '#E0E0E0';

    return (
        <ResponsiveContainer width="100%" height={300}>
            <BarChart
                data={data}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
                <XAxis
                    dataKey="date"
                    tickFormatter={formatDate}
                    tickLine={false}
                    axisLine={{ stroke: lineColor }}
                />
                <YAxis
                    tickLine={false}
                    axisLine={{ stroke: lineColor }}
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip
                    cursor={cursorStyle}
                    content={<CustomToolTip freq={freq} />}
                    wrapperStyle={{ zIndex: 99 }}
                />
                {showLegend && <Legend iconType="square" />}
                <Bar
                    name="Facturé"
                    dataKey="revenue_untaxed"
                    stackId="revenue"
                    fill={theme.palette.success.main}
                    radius={[3, 3, 0, 0]}
                />
                <Bar
                    name="Budgets à facturer"
                    dataKey="projected_budget"
                    stackId="revenue"
                    fill={theme.palette.warning.main}
                />
                <Bar
                    name="Leads (prévision)"
                    dataKey="projected_leads"
                    stackId="revenue"
                    fill={theme.palette.info.main}
                />
            </BarChart>
        </ResponsiveContainer>
    );
};

export default InvoicingRevenueChart;
